import { getTagFromNode, useBaseMetadataForLinkedEntities, useOptionsetMetadata, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  FieldSelectOptions,
  FormFieldDefinition,
  FormOpenMode,
  OptionDefinition,
  OptionsetFieldDefinition,
  OptionSetModel,
  OptionsetValue,
  OptionsetVisualization,
} from '@msdk/definitions'
import { FormField, FormFieldLabel, styled } from '@msdk/ui'
import React from 'react'
import { useParams } from 'react-router-dom'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'
import { ConditionalOptionsetRenderer, OptionsetType } from './ConditionalOptionsetRenderer'
import { filterOptions } from './filter-options'

// remove unnecessary padding-top when the field does not show its label
const StyledFormField = styled(FormField, {
  shouldForwardProp: (prop) => prop !== 'showLabel',
})<{ showLabel?: boolean }>`
  ${(props) =>
    props?.showLabel === false || props?.showLabel == null
      ? `
        &:has(.MuiFormGroup-root) {
          padding-top: 0;
        }
      `
      : ''}
`

export function OptionsetField({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as OptionsetFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)

  const { moduleName } = useParams()
  const t = useTranslation()
  const [value, setValue] = useFieldValue(node, onChange, 0)
  const baseMetadata = useBaseMetadataForLinkedEntities(fieldDef.entityName, formDef.formName)
  const optModel = useOptionsetMetadata(baseMetadata?.msProvider as string, fieldDef.family) as OptionSetModel
  const options = filterOptions(fieldDef, optModel?.allOptions, node.value.selectOptions as FieldSelectOptions, t)

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const onFieldChange = (val: string | string[]) => {
    if (Array.isArray(val) && val.length > 0) {
      const values: string[] = []
      let normalizedInputVal: string[] = val

      //handle exclusive attribute on optionset model
      const exlusiveOpt = optModel?.allOptions.find((o) => o.exclusiveValue) as OptionDefinition
      if (exlusiveOpt) {
        const exclusiveOptValue = exlusiveOpt.value
        const hadExclusive = (value && (value as OptionsetValue).value?.split('|'))?.includes(exclusiveOptValue)
        const hasExclusive = val.includes(exclusiveOptValue)

        if (hasExclusive && !hadExclusive) {
          // user just selected exclusive value, only that one must be kept
          normalizedInputVal = [exclusiveOptValue]
        } else if (hasExclusive && val.length > 1) {
          /* exclusive value selected together with other values, not admitted
          typical case: exclusive value previously selected, and user added in second time another value
          so the exclusive value has no sense anymore and must be filtered out */
          normalizedInputVal = val.filter((v) => v !== exclusiveOptValue)
        }
      }

      normalizedInputVal.forEach((v) => {
        const optEntry = getOptEntry(v)
        values.push(optEntry?.value as string)
      })

      const fieldValue = new OptionsetValue(values.join('|'), fieldDef.family)
      node.value.isDirty = true
      setValue(fieldValue)
    } else if (val && !Array.isArray(val)) {
      const optEntry = getOptEntry(val)
      const fieldValue = new OptionsetValue(optEntry?.value, fieldDef.family, val)
      node.value.isDirty = true
      setValue(fieldValue)
    } else {
      node.value.isDirty = true
      setValue(null)
    }
  }

  const getValue = (): any => {
    if (value) {
      const optValue = value as OptionsetValue
      const splitted = optValue.value?.split('|') as string[]
      const items = splitted.map((v) => {
        const entry = getOptEntry(v)
        return {
          label: t(entry.idml, entry.description),
          value: entry.value,
        }
      })
      return items?.length > 1 ? items : items[0]
    }
    return []
  }

  const meetsAutomaticRerquirements = (): boolean => {
    const concatenated = options.join('|')
    return options.length < 5 && concatenated.length < 40 && fieldDef.visualization === OptionsetVisualization.Automatic
  }

  const getOptEntry = (val: string): OptionDefinition => {
    let opt = optModel?.allOptions.find((o) => o.value === val) as OptionDefinition
    if (!opt) opt = optModel?.allOptions.find((o) => t(o.idml, o.description) === val) as OptionDefinition
    return opt
  }

  const getOptionsetType = (): OptionsetType => {
    let result: OptionsetType = 'Dropdown'

    if (
      !fieldDef.multipleSelection &&
      !meetsAutomaticRerquirements() &&
      (fieldDef.visualization === OptionsetVisualization.Automatic ||
        fieldDef.visualization === OptionsetVisualization.Dropdown)
    ) {
      result = 'Dropdown'
    } else if (
      fieldDef.multipleSelection &&
      !meetsAutomaticRerquirements() &&
      (fieldDef.visualization === OptionsetVisualization.Automatic ||
        fieldDef.visualization === OptionsetVisualization.Dropdown)
    ) {
      result = 'DropdownMultiselect'
    } else if (
      !fieldDef.multipleSelection &&
      (meetsAutomaticRerquirements() || fieldDef.visualization === OptionsetVisualization.RadioButton)
    ) {
      result = 'RadioButton'
    } else if (
      fieldDef.multipleSelection &&
      (meetsAutomaticRerquirements() || fieldDef.visualization === OptionsetVisualization.RadioButton)
    ) {
      result = 'RadioButtonMultiple'
    } else if (fieldDef.multipleSelection && fieldDef.visualization === OptionsetVisualization.Checkbox) {
      result = 'Checkbox'
    } else if (!fieldDef.multipleSelection && fieldDef.visualization === OptionsetVisualization.Checkbox) {
      result = 'CheckboxSingle'
    } else if (fieldDef.visualization === OptionsetVisualization.SlideToggle) {
      result = 'SlideToggle'
    }

    return result
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)
  return (
    <StyledFormField
      showLabel={fieldArgs?.showLabel}
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fieldArgs?.showLabel &&
        (getOptionsetType() === 'RadioButton' ||
          getOptionsetType() === 'RadioButtonMultiple' ||
          getOptionsetType() === 'Checkbox') && (
          <FormFieldLabel shrink={true} hasEndAdornment={true} filterField={fieldArgs?.filterField}>
            {fieldLabel}
          </FormFieldLabel>
        )}

      {fieldArgs?.showLabel &&
        (getOptionsetType() === 'Dropdown' ||
          getOptionsetType() === 'DropdownMultiselect' ||
          getOptionsetType() === 'SlideToggle') && (
          <FormFieldLabel hasEndAdornment={true} filterField={fieldArgs?.filterField}>
            {fieldLabel}
          </FormFieldLabel>
        )}
      <ConditionalOptionsetRenderer
        label={fieldArgs?.showLabel ? fieldLabel : ''}
        options={options}
        value={getValue()}
        onChange={onFieldChange}
        type={getOptionsetType()}
        disabled={isReadOnly}
        direction={fieldDef.direction}
        canBeUnselected={fieldDef.canBeUnselected}
        beginInteraction={fieldArgs?.beginInteraction}
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </StyledFormField>
  )
}
